import { useEffect, useMemo, useState } from "react";
import { Modal, Select, message } from "antd";
import { useRecoilState } from "recoil";
import { Button as MhmButton } from "../../misc/Button";
import { brandKitsState } from "../../../../state/atoms";
import { BrandKitDto, LocationDto } from "../../../../generated/dashboard-client";
import BrandKitApi from "../../../../api/BrandKitApi";
import { env } from "../../../../env/env";
import "./AssignKitToLocationModal.scss";

interface Props {
  open: boolean;
  location: LocationDto | null;
  currentKit: BrandKitDto | null;
  onClose: () => void;
  onAssigned?: () => void;
}

// Location-scoped counterpart to AssignBrandToLocationModal: given ONE location, pick the single
// Brand Kit it should belong to. A location has at most one kit, so a "Change" removes it from its
// current kit and adds it to the chosen one.
export function AssignKitToLocationModal({ open, location, currentKit, onClose, onAssigned }: Props) {
  const [brandKits, setBrandKits] = useRecoilState(brandKitsState);
  const [selectedKitId, setSelectedKitId] = useState<string | undefined>();
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (open) setSelectedKitId(currentKit?.id);
  }, [open, currentKit]);

  const sortedKits = useMemo(
    () =>
      [...brandKits].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
      ),
    [brandKits]
  );

  const locationId = location?.internalId;
  const changed = selectedKitId !== currentKit?.id;

  const close = () => {
    if (assigning) return;
    onClose();
  };

  const handleAssign = async () => {
    if (!locationId || !selectedKitId || !changed) {
      onClose();
      return;
    }
    const oldKitId = currentKit?.id;
    setAssigning(true);
    try {
      // Remove from the previous kit first (assignLocation only adds; a location may not be on two kits).
      if (oldKitId) {
        await BrandKitApi.unassignLocation(oldKitId, locationId);
      }
      await BrandKitApi.assignLocation(selectedKitId, locationId);

      // Update locationIds locally rather than replacing the kit objects — the assign/unassign
      // responses are light DTOs (no asset/template counts or logo), so a wholesale replace would
      // blank those fields elsewhere in the UI.
      setBrandKits((prev) =>
        prev.map((b) => {
          if (b.id === oldKitId) {
            return { ...b, locationIds: (b.locationIds || []).filter((id) => id !== locationId) };
          }
          if (b.id === selectedKitId) {
            const ids = b.locationIds || [];
            return ids.includes(locationId) ? b : { ...b, locationIds: [...ids, locationId] };
          }
          return b;
        })
      );
      message.success("Brand Kit assigned");
      onAssigned?.();
      onClose();
    } catch {
      message.error("Failed to assign Brand Kit");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Modal className="assign-kit-modal clean one" open={open} onCancel={close} footer={null}>
      <header>
        <h2>Assign Brand to Location</h2>
      </header>
      <p className="assign-kit-subtitle">Choose brand to assign to location</p>

      <div className="assign-kit-body">
        <Select
          showSearch
          className="assign-kit-select"
          size="large"
          style={{ width: "100%" }}
          placeholder="Select brand"
          value={selectedKitId}
          onChange={setSelectedKitId}
          optionFilterProp="label"
          optionLabelProp="label"
          dropdownClassName="assign-kit-dropdown"
        >
          {sortedKits.map((kit) => {
            const logoPath = (kit as any).firstLogoPath as string | undefined;
            const logoUrl = logoPath ? `${env.s3Path}/${logoPath}` : null;
            const name = kit.name || "Untitled";
            const initial = name.trim().charAt(0).toUpperCase() || "?";
            return (
              <Select.Option key={kit.id} value={kit.id!} label={name} className="assign-kit-option">
                <span className="assign-kit-option-row">
                  {logoUrl ? (
                    <img className="assign-kit-option-logo" src={logoUrl} alt={name} />
                  ) : (
                    <span className="assign-kit-option-logo placeholder">{initial}</span>
                  )}
                  <span className="assign-kit-option-name">{name}</span>
                </span>
              </Select.Option>
            );
          })}
        </Select>
      </div>

      <footer>
        <MhmButton type="Cancel" onClick={close}>
          <span>Cancel</span>
        </MhmButton>
        <MhmButton
          type="Primary"
          onClick={handleAssign}
          loading={assigning}
          disabled={!selectedKitId || !changed}
        >
          <span>{currentKit ? "Change" : "Assign"}</span>
        </MhmButton>
      </footer>
    </Modal>
  );
}
